export const getDMChannelId = (userId1: string, userId2: string): string => {
  const sortedIds = [userId1, userId2].sort();
  return `dm-${sortedIds[0]}-${sortedIds[1]}`;
};

export const formatMessageTime = (timestamp: any): string => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days === 0) {
    return date.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  } else if (days === 1) {
    return '어제';
  } else if (days < 7) {
    return `${days}일 전`;
  } else {
    return date.toLocaleDateString('ko-KR', { 
      month: 'short', 
      day: 'numeric' 
    });
  }
};

export const isImageFile = (fileName: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'tiff', 'tif', 'heic', 'heif'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return imageExtensions.includes(extension || '');
};

export const isVideoFile = (fileName: string): boolean => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'm4v', '3gp'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return videoExtensions.includes(extension || '');
};

export const isAudioFile = (fileName: string): boolean => {
  const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'opus'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return audioExtensions.includes(extension || '');
};

export const isPDFFile = (fileName: string): boolean => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension === 'pdf';
};

export const isWordFile = (fileName: string): boolean => {
  const wordExtensions = ['doc', 'docx', 'odt', 'rtf'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return wordExtensions.includes(extension || '');
};

export const isExcelFile = (fileName: string): boolean => {
  const excelExtensions = ['xls', 'xlsx', 'csv', 'ods'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return excelExtensions.includes(extension || '');
};

export const isPowerPointFile = (fileName: string): boolean => {
  const pptExtensions = ['ppt', 'pptx', 'odp'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return pptExtensions.includes(extension || '');
};

export const isCodeFile = (fileName: string): boolean => {
  const codeExtensions = [
    'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'cc', 'h', 'hpp',
    'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt', 'scala', 'r', 'm', 'mm',
    'html', 'css', 'scss', 'sass', 'less', 'xml', 'json', 'yaml', 'yml',
    'md', 'sql', 'sh', 'bash', 'zsh', 'fish', 'ps1', 'bat', 'cmd',
    'vue', 'svelte', 'astro', 'dart', 'lua', 'pl', 'pm', 'tcl', 'groovy',
    'gradle', 'makefile', 'dockerfile', 'tf', 'hcl', 'toml', 'ini', 'cfg',
    'env', 'properties', 'conf', 'config', 'gitignore', 'editorconfig'
  ];
  const extension = fileName.split('.').pop()?.toLowerCase();
  const fileNameLower = fileName.toLowerCase();

  // Check for special files without extensions
  const specialFiles = ['makefile', 'dockerfile', 'jenkinsfile', 'vagrantfile', 'procfile', 'rakefile'];
  if (specialFiles.some(name => fileNameLower.includes(name))) {
    return true;
  }

  return codeExtensions.includes(extension || '');
};

export const isTextFile = (fileName: string): boolean => {
  const textExtensions = ['txt', 'text', 'log', 'md', 'markdown', 'rst'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return textExtensions.includes(extension || '');
};

export const isArchiveFile = (fileName: string): boolean => {
  const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz'];
  const extension = fileName.split('.').pop()?.toLowerCase();
  return archiveExtensions.includes(extension || '');
};

export const getFileIcon = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  const iconMap: { [key: string]: string } = {
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    xls: '📊',
    xlsx: '📊',
    ppt: '📊',
    pptx: '📊',
    zip: '📦',
    rar: '📦',
    txt: '📄',
    json: '📋',
    xml: '📋',
    csv: '📊',
    mp3: '🎵',
    wav: '🎵',
    mp4: '🎬',
    mov: '🎬',
    avi: '🎬',
    jpg: '🖼️',
    jpeg: '🖼️',
    png: '🖼️',
    gif: '🖼️',
    svg: '🖼️',
  };
  
  return iconMap[extension || ''] || '📎';
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};